import {
  addIsoDays,
  INVOICE_TERMS_PRESETS,
  type InvoiceTermsPreset,
  invoiceDueDaysForTerms,
  isValidIsoDate,
} from '@clockwise/shared';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatIsoDate } from '@/lib/utils';

/**
 * The payment terms most invoices use, and the day count behind each.
 *
 * The settings and editor use this same list, and due-day arithmetic is always
 * derived from the selected label. Existing imported invoices may still carry
 * custom printed terms and an explicit due date, but settings cannot create a
 * new mismatch between the two.
 */
export const TERMS_PRESETS = INVOICE_TERMS_PRESETS;
export type TermsPreset = InvoiceTermsPreset;

/** The day count represented by a standard terms label. */
export function daysForTerms(terms: string): number | null {
  return invoiceDueDaysForTerms(terms);
}

/** Whole days from `from` to `to` (both `YYYY-MM-DD`), or null when either
 *  is not a date. UTC on the string form, so no timezone can shift it a day. */
export function daysBetweenIso(from: string, to: string): number | null {
  if (!isValidIsoDate(from) || !isValidIsoDate(to)) return null;
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** The date `days` after `invoiceDate`, or `''` when there is no usable
 *  invoice date to count from. */
export function dueDateFor(invoiceDate: string, days: number): string {
  return isValidIsoDate(invoiceDate) ? addIsoDays(invoiceDate, days) : '';
}

/**
 * Shared standard-terms picker. The caller stores the selected label and uses
 * the preset's day count to update its due value. On an invoice, each option
 * previews the resulting due date before it is selected.
 */
export function InvoiceTermsInput({
  id,
  invoiceDate,
  disabled,
  invalid,
  onPick,
  inputProps,
}: {
  id: string;
  /** When present, each preset previews the due date it would produce. The
   * settings page has no invoice date, so it uses the same picker without the
   * preview. */
  invoiceDate?: string;
  disabled?: boolean;
  invalid?: boolean;
  onPick: (preset: TermsPreset) => void;
  /** The registered field — terms stays an ordinary input, so a validation
   *  error can scroll to it like any other. */
  inputProps: UseFormRegisterReturn;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Input
            id={id}
            autoComplete="off"
            placeholder="Select terms"
            readOnly
            disabled={disabled}
            aria-invalid={invalid}
            className="pr-9"
            onClick={() => setOpen(true)}
            {...inputProps}
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Standard terms"
              className="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronDown className="size-4" aria-hidden />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="p-1"
        style={{ width: 'var(--radix-popper-anchor-width)', minWidth: '14rem' }}
      >
        {TERMS_PRESETS.map((preset) => {
          const due = invoiceDate === undefined ? '' : dueDateFor(invoiceDate, preset.days);
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => {
                onPick(preset);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent"
            >
              <span>{preset.label}</span>
              {due !== '' ? (
                <span className="text-muted-foreground text-xs tabular-nums">
                  Due {formatIsoDate(due)}
                </span>
              ) : null}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
